import React, { PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { usePrevious } from "react-use";
import { skipToken } from "@reduxjs/toolkit/query";
import { ReactFlowProvider, useNodesInitialized, useReactFlow } from "@xyflow/react";

import { useGetAccountModulesQuery, useGetVersionSpliceTableQuery, useGetVersionStatusQuery, useLazyGetFeatureQuery } from "fond/api";
import { useFeatureFlag } from "fond/featureFlags";
import { LayoutContext } from "fond/layout/LayoutProvider";
import { selectWidget } from "fond/layout/widgets";
import { zoomTo } from "fond/map/redux";
import { getCurrentProject, selectFeature } from "fond/project/redux";
import {
  SelectFeatureOptions,
  SelectFeatureTarget,
  TopologicalViewContext,
  TopologicalViewContextProps,
} from "fond/topologicalView/TopologicalViewContext";
import { SpliceTableCable, SpliceTableNode, Store, Widget } from "fond/types";
import { useAppDispatch } from "fond/utils/hooks";
import { accountModuleCheck, Actions } from "fond/utils/permissions";

type ResolveFeatureResult =
  | undefined
  | { type: "node"; node: SpliceTableNode; topologicalNode: SpliceTableNode | undefined }
  | { type: "cable"; cable: SpliceTableCable };

/**
 * Return the parent ID of a leaf node, if the node is a leaf.
 */
const tryGetLeafParentId = (node: SpliceTableNode): string | undefined => {
  if (node.OutgoingCables.length === 0 && node.IncomingCables.length === 1) {
    return node.IncomingCables[0].StartNode.ID;
  }
  return undefined;
};

const InnerTopologicalViewContextProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const dispatch = useAppDispatch();
  const [getFeature] = useLazyGetFeatureQuery();
  const layout = useContext(LayoutContext);
  const [selectedNode, setSelectedNode] = useState<string | null>(null);
  const { fitView } = useReactFlow();
  const nodesInitialized = useNodesInitialized();
  const project = useSelector((state: Store) => getCurrentProject(state.project));
  const versionId = useSelector((state: Store) => state.project.versionId);

  const { data: accountModules } = useGetAccountModulesQuery(project?.Account.ID ?? skipToken);
  const topologicalViewEnabled = useFeatureFlag("topological_splice_view") && accountModuleCheck(accountModules, Actions.PROJECT_TOPOLOGICAL_VIEW);
  const topologicalViewAvailable = topologicalViewEnabled && project?.EntityType === "project" && project?.Type === "planner" && !!versionId;

  const {
    data: spliceNodes,
    isFetching: isFetchingSpliceTables,
    isError: isErrorSpliceTables,
    refetch: refetchSpliceTables,
  } = useGetVersionSpliceTableQuery(versionId, { skip: !topologicalViewAvailable });
  const { data: versionStatus } = useGetVersionStatusQuery(versionId, { skip: !topologicalViewAvailable });
  const previousStatus = usePrevious(versionStatus?.Status);

  // If we observe the workflow just completed, then refetch the splice tables to ensure we have the latest data.
  useEffect(() => {
    if (versionStatus?.Status === "Complete" && previousStatus !== "Complete") {
      refetchSpliceTables();
    }
  }, [versionStatus?.Status, previousStatus, refetchSpliceTables]);

  const nodes: Record<string, SpliceTableNode | undefined> = useMemo(
    () => Object.fromEntries(spliceNodes?.map((node) => [node.ID, node]) ?? []),
    [spliceNodes]
  );
  const cables: Record<string, SpliceTableCable | undefined> = useMemo(
    () => Object.fromEntries(spliceNodes?.flatMap((node) => node.IncomingCables.map((cable) => [cable.FondID, cable])) ?? []),
    [spliceNodes]
  );
  const cableIds: Record<string, SpliceTableCable | undefined> = useMemo(
    () =>
      Object.fromEntries(
        spliceNodes?.flatMap((node) => node.IncomingCables.filter((cable) => cable.ID !== null).map((cable) => [cable.ID, cable])) ?? []
      ),
    [spliceNodes]
  );

  // Resolve a feature based on its API or Fond ID.
  const resolveFeature = useCallback(
    (target: SelectFeatureTarget): ResolveFeatureResult => {
      // Cables do not support ID lookups (not all cables have an ID, unfortunately)
      if ("id" in target) {
        const node = nodes[target.id];
        if (!node) {
          const cable = cableIds[target.id];
          return cable ? { type: "cable", cable } : undefined;
        }

        // Leaf nodes are resolved to their parent node in the topological view.
        const leafParentId = tryGetLeafParentId(node);
        const topologicalNode = leafParentId ? nodes[leafParentId] : undefined;

        return { type: "node", node, topologicalNode };
      }

      const cable = cables[target.fondId];
      return cable ? { type: "cable", cable } : undefined;
    },
    [nodes, cables, cableIds]
  );

  const hasFeature = useCallback((target: SelectFeatureTarget) => resolveFeature(target) !== undefined, [resolveFeature]);

  // Zoom to the selected node.
  useEffect(() => {
    if (selectedNode && nodesInitialized) {
      try {
        const node = nodes[selectedNode];
        if (!node) return;

        // Leaf nodes are merged into their parent node in the topological view,
        // so we can't zoom to them directly.
        let targetNode = node.ID;
        if (node.OutgoingCables.length === 0 && node.IncomingCables.length === 1) {
          targetNode = node.IncomingCables[0].StartNode.ID;
        }
        fitView({
          nodes: [{ id: targetNode }],
          padding: 0.25,
        });
      } finally {
        setSelectedNode(null);
      }
    }
  }, [nodesInitialized, selectedNode, fitView, nodes]);

  // Select a feature based on its API/Fond ID.
  const selectFeatureById = useCallback(
    (target: SelectFeatureTarget, options?: SelectFeatureOptions) => {
      const shouldHighlightFeature = options?.highlightFeature ?? false;
      const shouldShowProperties = options?.showProperties ?? false;
      const shouldZoomMap = options?.zoomMap ?? false;
      const shouldZoomTopologicalView = options?.zoomTopologicalView ?? false;
      const end = options?.end ?? "downstream";

      const resolved = resolveFeature(target);
      if (!resolved) {
        throw new Error("Feature not found");
      }

      let targetNode;
      let targetFeature;

      // In the topological view we don't want to zoom to an edge; rather we want to zoom to
      // one of the nodes at either end of the edge.
      if (resolved.type === "cable") {
        const { cable } = resolved;
        targetNode = end === "downstream" ? cable.EndNode.ID : cable.StartNode.ID;
        targetFeature = cable.ID ? { id: cable.ID, layerKey: target.layerKey } : null;
      } else {
        const { node, topologicalNode } = resolved;
        targetNode = topologicalNode ? topologicalNode.ID : node.ID;
        targetFeature = { id: node.ID, layerKey: target.layerKey };
      }

      // Anything involving a map interaction requires us to have the full feature data.
      if (targetFeature && (shouldHighlightFeature || shouldZoomMap)) {
        getFeature(targetFeature.id, true)
          .unwrap()
          .then((rawFeature) => {
            const feature = {
              ...rawFeature,
              properties: {
                ...rawFeature.properties,
                layerId: targetFeature.layerKey,
                featureId: targetFeature.id,
              },
              source: targetFeature.layerKey,
            };

            if (shouldHighlightFeature) dispatch(selectFeature(feature, null, false));
            if (shouldShowProperties && layout?.model) selectWidget(layout.model, Widget.Properties);
            if (shouldZoomMap) dispatch(zoomTo([feature]));
          });
      }

      if (shouldZoomTopologicalView) {
        if (layout?.model) selectWidget(layout.model, Widget.TopologicalView);
        setSelectedNode(targetNode);
      }
    },
    [dispatch, getFeature, layout?.model, resolveFeature]
  );

  const value: TopologicalViewContextProps = useMemo(() => {
    return {
      selectFeature: selectFeatureById,
      hasFeature,
      isAvailable: topologicalViewAvailable,
      isEnabled: topologicalViewEnabled,
      data: {
        isFetching: isFetchingSpliceTables,
        isError: isErrorSpliceTables,
        spliceNodes,
      },
    };
  }, [selectFeatureById, hasFeature, topologicalViewAvailable, topologicalViewEnabled, isFetchingSpliceTables, isErrorSpliceTables, spliceNodes]);

  return <TopologicalViewContext.Provider value={value}>{children}</TopologicalViewContext.Provider>;
};

const TopologicalViewContextProvider: React.FC<PropsWithChildren> = ({ children }) => {
  return (
    <ReactFlowProvider>
      <InnerTopologicalViewContextProvider>{children}</InnerTopologicalViewContextProvider>
    </ReactFlowProvider>
  );
};

export default TopologicalViewContextProvider;
